import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Collapse,
} from '@mui/material';
import { Add, Delete, Refresh, ExpandMore, ExpandLess, AccessTime } from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';

export default function Sessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [dialog, setDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ session: '', checkInTime: '', checkOutTime: '' });

  const load = async () => {
    setLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getSessions, {});
    setSessions(data?.session || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this session?')) return;
    const data = await InvokeApi(Config.main_url + apiList.deleteSession, { id });
    if (data?.code === '200') load();
    else alert(data?.message || 'Failed to delete');
  };

  const handleSave = async () => {
    if (!form.session || !form.checkInTime || !form.checkOutTime) {
      alert('All fields are required');
      return;
    }
    setSaving(true);
    const toHMS = (t) => t.length === 5 ? t + ':00' : t;
    const data = await InvokeApi(Config.main_url + apiList.addSession, {
      session: form.session,
      checkInTime: toHMS(form.checkInTime),
      checkOutTime: toHMS(form.checkOutTime),
    });
    setSaving(false);
    if (data?.code === '200') {
      setDialog(false);
      setForm({ session: '', checkInTime: '', checkOutTime: '' });
      load();
    } else {
      alert(data?.message || 'Failed to add session');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Sessions Manager</Typography>
          <Typography variant="body2" color="text.secondary">Manage attendance check-in/out sessions</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={load} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => setDialog(true)}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            Add Session
          </Button>
        </Box>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress sx={{ color: '#1a237e' }} />
          </Box>
        ) : sessions.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <AccessTime sx={{ fontSize: 52, color: '#e8eaf6', mb: 1 }} />
            <Typography color="text.secondary">No sessions found</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#f5f6fa' }}>
                  {['#', 'Session Name', 'Timings', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((item, i) => (
                  <TableRow key={item.ID || i} hover>
                    <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{i + 1}</TableCell>
                    <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>Session — {item.session}</TableCell>
                    <TableCell>
                      <Box sx={{ background: 'transparent', borderBottom: '0px solid #f0f0f0' }}>
                        <Box sx={{ display: 'flex', gap: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label="Check In" size="small" sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: 11 }} />
                            <Typography fontWeight={700} fontSize={14} color="#2e7d32">{item.check_in_time}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label="Check Out" size="small" sx={{ background: '#ffebee', color: '#c62828', fontWeight: 600, fontSize: 11 }} />
                            <Typography fontWeight={700} fontSize={14} color="#c62828">{item.check_out_time}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(item.ID)} sx={{ color: '#c62828' }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e' }}>Add Session</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Session Name *"
                value={form.session} onChange={(e) => setForm(p => ({ ...p, session: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" type="time" label="Check In Time *"
                value={form.checkInTime} onChange={(e) => setForm(p => ({ ...p, checkInTime: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" type="time" label="Check Out Time *"
                value={form.checkOutTime} onChange={(e) => setForm(p => ({ ...p, checkOutTime: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
